import { useEffect } from 'react'
import { theme } from '../theme'
import { ModalShell } from './ModalShell'

// Themed replacements for window.alert / window.confirm. Both render through
// ModalShell so they fade/scale in like the rest of our modals.

type AlertProps = {
  open: boolean
  title?: string
  message: string
  buttonLabel?: string
  onClose: () => void
  // Stack above other modals (default 80 vs ModalShell's 60).
  zIndex?: number
}

export function AlertDialog({
  open, title, message, buttonLabel = 'OK', onClose, zIndex = 80,
}: AlertProps) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); onClose() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  return (
    <ModalShell
      open={open} onClose={onClose} zIndex={zIndex} autoHeight
      panelStyle={{ width: 'min(380px, 100%)', padding: '20px 22px', gap: 14 }}
    >
      {title && (
        <div style={{
          fontFamily: theme.sans, fontSize: 10, letterSpacing: 1.4, fontWeight: 600,
          textTransform: 'uppercase', color: theme.muted,
        }}>{title}</div>
      )}
      <div style={{
        fontFamily: theme.serif, fontSize: 18, fontWeight: theme.headWeight,
        color: theme.ink, lineHeight: 1.35,
      }}>{message}</div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
        <button onClick={onClose} style={btnPrimary} autoFocus>{buttonLabel}</button>
      </div>
    </ModalShell>
  )
}

type ConfirmProps = {
  open: boolean
  title?: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  danger?: boolean
  onConfirm: () => void
  onCancel: () => void
  zIndex?: number
}

export function ConfirmDialog({
  open, title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel',
  danger = false, onConfirm, onCancel, zIndex = 80,
}: ConfirmProps) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onCancel() }
      else if (e.key === 'Enter') { e.preventDefault(); onConfirm() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onConfirm, onCancel])

  return (
    <ModalShell
      open={open} onClose={onCancel} zIndex={zIndex} autoHeight
      panelStyle={{ width: 'min(380px, 100%)', padding: '20px 22px', gap: 14 }}
    >
      {title && (
        <div style={{
          fontFamily: theme.sans, fontSize: 10, letterSpacing: 1.4, fontWeight: 600,
          textTransform: 'uppercase', color: theme.muted,
        }}>{title}</div>
      )}
      <div style={{
        fontFamily: theme.serif, fontSize: 18, fontWeight: theme.headWeight,
        color: theme.ink, lineHeight: 1.35,
      }}>{message}</div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 2 }}>
        <button onClick={onCancel} style={btnSecondary}>{cancelLabel}</button>
        <button onClick={onConfirm} style={danger ? btnDanger : btnPrimary} autoFocus>
          {confirmLabel}
        </button>
      </div>
    </ModalShell>
  )
}

const btnSecondary: React.CSSProperties = {
  all: 'unset', cursor: 'pointer',
  padding: '8px 14px', borderRadius: theme.radius,
  fontFamily: theme.sans, fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
  textTransform: 'uppercase', color: theme.ink,
  border: `1px solid ${theme.lineStrong}`,
}
const btnPrimary: React.CSSProperties = {
  all: 'unset', cursor: 'pointer',
  padding: '8px 14px', borderRadius: theme.radius,
  fontFamily: theme.sans, fontSize: 12, fontWeight: 700, letterSpacing: 0.4,
  textTransform: 'uppercase', color: theme.surface, background: theme.ink,
}
const btnDanger: React.CSSProperties = {
  all: 'unset', cursor: 'pointer',
  padding: '8px 14px', borderRadius: theme.radius,
  fontFamily: theme.sans, fontSize: 12, fontWeight: 700, letterSpacing: 0.4,
  textTransform: 'uppercase', color: theme.surface, background: 'oklch(0.42 0.16 28)',
}
